import { typedAction } from "../helpers";
import {
  SET_FILES,
  UPDATE_FILE_NAME,
  SET_FILES_CAN_OVERWRITE,
  SET_FILES_ONE_DRIVE_OVERWRITE_OPTION_IS_SELECTED,
  SET_FILES_EXPANDER,
  RESET_FILES,
  ADD_UPLOADED_FILE,
  ADD_UPLOADED_FILES,
  DELETE_FILES_BY_ORIGINALNAME,
  SET_FILE,
  SET_COMPOSE_FILES,
  SET_COMPOSE_VISIBILITY,
  RESET_COMPOSE
} from "./actionTypes";
import { ComposeFiles, FileState } from "../models/reduxModels";
import { IFileToUpload } from "../../interfaces/IFileToUpload";
import { OneDriveOverWriteEnum } from "../../components/enums/oneDriveOverWriteEnum";
import { validSaveAsPdfExtentions } from "../../config/constants";

const initialState: FileState = { files: [], canOverwrite: true, expanderIsOpen: true, oneDriveOverwriteOption: OneDriveOverWriteEnum.notSelected, compose: { selectedFiles: [], isGraphQuery: false, isVisible: false } };

export const setFiles = (files: IFileToUpload[]) => {
  return typedAction(SET_FILES, files);
};

export const setFile = (file: IFileToUpload) => {
  return typedAction(SET_FILE, file);
};

export const setComposeFiles = (data: ComposeFiles) => {
  return typedAction(SET_COMPOSE_FILES, data);
};

export const setComposeVisibility = (isVisible: boolean) => {
  return typedAction(SET_COMPOSE_VISIBILITY, isVisible);
};

export const resetCompose = () => {
  return typedAction(RESET_COMPOSE);
};

export const addUploadedFile = (file: IFileToUpload) => {
  return typedAction(ADD_UPLOADED_FILE, file);
};

export const addUploadedFiles = (files: IFileToUpload[]) => {
  return typedAction(ADD_UPLOADED_FILES, files);
};

export const resetFiles = (files: IFileToUpload[]) => {
  return typedAction(RESET_FILES, files);
};

export const setFilesCanOverwrite = (canOverWrite: boolean) => {
  return typedAction(SET_FILES_CAN_OVERWRITE, canOverWrite);
};

export const setFileExpander = (expanderIsOpen: boolean) => {
  return typedAction(SET_FILES_EXPANDER, expanderIsOpen);
};

export const setOneDriveOverwriteOptionSelected = (oneDriveOverWriteOptionSelected: OneDriveOverWriteEnum) => {
  return typedAction(SET_FILES_ONE_DRIVE_OVERWRITE_OPTION_IS_SELECTED, oneDriveOverWriteOptionSelected);
};

export const resetDuplicateFilesAndOverWriteSettings = (files: IFileToUpload[]) => {
  if (files.some(item => item.isDuplicate)) {
    const updatedFiles: IFileToUpload[] = files.map(item => ({ ...item, isDuplicate: false, filePreCommitResult: null }));
    return typedAction(RESET_FILES, updatedFiles);
  }

  return typedAction(RESET_FILES, files);
};

export const deleteFilesByOriginalName = (originalFileNames: string[]) => {
  return typedAction(DELETE_FILES_BY_ORIGINALNAME, originalFileNames);
};

export const updateFileName = (nameInfo: { name: string; id: string }) => {
  return typedAction(UPDATE_FILE_NAME, nameInfo);
};

type FileAction = ReturnType<
  | typeof setFiles
  | typeof setComposeFiles
  | typeof setComposeVisibility
  | typeof resetCompose
  | typeof setFile
  | typeof addUploadedFile
  | typeof addUploadedFiles
  | typeof deleteFilesByOriginalName
  | typeof updateFileName
  | typeof setFilesCanOverwrite
  | typeof setOneDriveOverwriteOptionSelected
  | typeof setFileExpander
  | typeof resetFiles
>;

export function fileReducer(state = initialState, action: FileAction): FileState {
  switch (action.type) {
    case SET_FILES:
      return {
        ...state,
        files: setFileIndex(action.payload),
      };
    case SET_FILE:
      return {
        ...state,
        files: state.files.map(
          (file) => file.id === action.payload.id ? { ...action.payload, canSaveAsPDF: validSaveAsPdfExtentions.some(ext => ext === file.extension) } : { ...file }
        )
      };
    case SET_COMPOSE_FILES:
      return {
        ...state,
        compose: {
          ...state.compose,
          selectedFiles: action.payload.selectedFiles.map(file => {
            return {
              ...file
            }
          }),
          isGraphQuery: action.payload.isGraphQuery
        }
      };
    case SET_COMPOSE_VISIBILITY:
      return {
        ...state,
        compose: {
          ...state.compose,
          isVisible: action.payload
        }
      };
    case RESET_COMPOSE:
      return {
        ...state,
        compose: {
          selectedFiles: [],
          isGraphQuery: false,
          isVisible: false
        }
      };
    case ADD_UPLOADED_FILE:
      return { ...state, files: [...state.files, action.payload] };
    case ADD_UPLOADED_FILES:
      return { ...state, files: state.files.concat(action.payload) };
    case RESET_FILES:
      return {
        ...state,
        files: setFileIndex(action.payload),
        canOverwrite: true,
        oneDriveOverwriteOption: OneDriveOverWriteEnum.notSelected,
        expanderIsOpen: action.payload.some(item => item.isDuplicate) || state.expanderIsOpen
      };
    case SET_FILES_CAN_OVERWRITE: {
      return { ...state, canOverwrite: action.payload };
    }
    case SET_FILES_EXPANDER: {
      return { ...state, expanderIsOpen: action.payload };
    }
    case SET_FILES_ONE_DRIVE_OVERWRITE_OPTION_IS_SELECTED: {
      return { ...state, canOverwrite: true, oneDriveOverwriteOption: action.payload };
    }
    case DELETE_FILES_BY_ORIGINALNAME:
      return {
        ...state,
        files: state.files.filter(item => !action.payload.some(originalName => (item.originalName + "." + item.extension) == originalName))
      }
    case UPDATE_FILE_NAME:
      return {
        ...state,
        files: state.files.map((item) => {
          return mapUpdateFileEntry(item, action.payload.name, "name", action.payload.id, item.id);
        })
      };
    default:
      return state;
  }
}

const setFileIndex = (files: IFileToUpload[]): IFileToUpload[] => {
  return files.map((file, index) => ({
    ...file,
    index,
    canSaveAsPDF: validSaveAsPdfExtentions.some(ext => ext === file.extension)
  }));
}

const mapUpdateFileEntry = (
  item: IFileToUpload,
  value: any,
  propertyName: string,
  idToUpdate: string,
  currentId: string
) => {
  return currentId !== idToUpdate ? item : { ...item, [propertyName]: value };
};
